import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Chip,
  Divider,
  Button
} from '@mui/material';
import { ArrowBack, CalendarToday, Person, AccessTime } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { havenColors } from '../../theme/theme';

interface BlogPostProps {
  title: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  excerpt: string;
  content: React.ReactNode;
}

const BlogPostTemplate: React.FC<BlogPostProps> = ({ 
  title, 
  author, 
  date, 
  readTime, 
  category, 
  excerpt, 
  content 
}) => {
  const navigate = useNavigate();

  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          py: 4,
          background: `linear-gradient(135deg, ${havenColors.primaryTeal} 0%, ${havenColors.darkTeal} 100%)`,
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/resources')}
            sx={{
              color: 'white',
              mb: 3,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back to Resources
          </Button>
          
          <Chip 
            label={category} 
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              mb: 2
            }} 
          />
          
          <Typography variant="h1" sx={{ 
            fontSize: { xs: '2rem', md: '2.8rem' }, 
            fontWeight: 700, 
            mb: 3,
            lineHeight: 1.2
          }}>
            {title}
          </Typography>
          
          <Typography variant="h6" sx={{ 
            mb: 4, 
            opacity: 0.9,
            fontSize: '1.2rem',
            lineHeight: 1.5
          }}>
            {excerpt}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person sx={{ fontSize: '1rem' }} />
              <Typography variant="body2">By {author}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday sx={{ fontSize: '1rem' }} />
              <Typography variant="body2">{date}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime sx={{ fontSize: '1rem' }} />
              <Typography variant="body2">{readTime}</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={{ 
          '& p': { mb: 3, lineHeight: 1.7, fontSize: '1.1rem' },
          '& h2': { 
            mt: 5, 
            mb: 3, 
            fontWeight: 600, 
            color: havenColors.darkGray,
            fontSize: '1.8rem'
          },
          '& h3': { 
            mt: 4, 
            mb: 2, 
            fontWeight: 600, 
            color: havenColors.darkGray,
            fontSize: '1.4rem'
          },
          '& ul': { mb: 3, pl: 3 },
          '& li': { mb: 1, lineHeight: 1.6 },
          '& blockquote': {
            borderLeft: `4px solid ${havenColors.primaryTeal}`,
            pl: 3,
            py: 2,
            backgroundColor: `${havenColors.primaryTeal}05`,
            fontStyle: 'italic',
            mb: 3
          }
        }}>
          {content}
        </Box>
      </Container>

      <Divider />

      {/* Footer CTA */}
      <Box sx={{ py: 6, backgroundColor: '#f8f9fa', textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: havenColors.darkGray }}>
            Ready to Improve Your Indoor Air Quality?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            Learn more about HAVEN's solutions for healthier indoor air.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/solutions')}
              sx={{
                backgroundColor: havenColors.primaryTeal,
                '&:hover': { backgroundColor: havenColors.darkTeal }
              }}
            >
              Explore Solutions
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                borderColor: havenColors.primaryTeal,
                color: havenColors.primaryTeal
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default BlogPostTemplate; 